import { inject,Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import {
    CancelProjectDeletionInput,
    DeleteProjectInput, DownloadProjectVersion,
    DownloadProjectVersionInput, graphql,
    Maybe,
    MediaclipMutationDeleteProjectArgs,
    MediaclipMutationDownloadProjectVersionArgs,
    MediaclipMutationInvalidateProjectStorageExternalPhotosCacheArgs,
MediaclipMutationSimulateProjectExpirationArgs,
    MediaclipMutationUpdateProjectVersionContentArgs,
    PhotoUnion,
    Project,
    ProjectVersion,
SimulateProjectExpiration, SimulateProjectExpirationInput,     UpdateProjectVersionContentInput } from 'app/shared/graph';
import { GraphDotnetService } from 'app/shared/graph';

@Injectable({ providedIn: 'root' })
export class ProjectsService {
    private readonly graphDotnetService = inject(GraphDotnetService);


    getProject(projectId: string): Observable<Maybe<Project> | undefined> {
        return this.graphDotnetService.query(graphql`
                query getProject($projectId: UUID!) {
                    project(id: $projectId) {
                        id
                        storeId
                        dateCreatedUtc
                        store {
                            id
                            subscriptionId
                        }
                        user {
                            id
                            storeUserId
                        }
                        currentVersionId
                        currentVersion {
                            id
                            thumbnailUrl
                        }
                        versions {
                            id
                            thumbnailUrl
                            lastModifiedUtc
                        }
                        properties {
                            name
                            value
                        }
                        designerOptions {
                            name
                            value
                        }
                        module
                        renderer
                        externalProductId
                        product {
                            id
                            label
                            thumbnail
                        }
                        theme {
                            id
                            label
                            thumbnail
                        }
                        title
                        sourceSharedProjectId
                        isDeleted
                        lastAccessDateUtc
                        deletionDateUtc
                        isDeletionCancellable
                        openLogsUrl
                        variableData {
                            items
                            variableDataResourceUrl
                            uploadedFilename
                        }
                    }
                }
            `,
            { projectId }
        ).pipe(map((result) => result.project));
    }


    getProjectOrders(projectId: string): Observable<Maybe<Project> | undefined> {
        return this.graphDotnetService.query(graphql`
                query getProject($projectId: UUID!) {
                    project(id: $projectId) {
                        orderLines(count: 100) {
                            id
                            storeOrderLineNumber
                            dateCreatedUtc
                            price {
                                currency
                                finalLinePrice
                                originalLinePrice
                                value
                            }
                            store {
                                id
                            }
                            order {
                                id
                                storeOrderId
                            }
                        }
                    }
                }
            `,
            { projectId }
        ).pipe(map((result) => result.project));
    }

    getProjectVersion(projectId: string, projectVersionId: number): Observable<Maybe<ProjectVersion> | undefined> {
        return this.graphDotnetService.query(graphql`
                query getProject($projectId: UUID!, $projectVersionId: Int!) {
                    project(id: $projectId) {
                        version(id: $projectVersionId) {
                            id
                            xml
                            lastModifiedUtc
                        }
                    }
                }
            `,
            { projectId, projectVersionId }
        ).pipe(map((result) => result.project?.version));
    }

    getProjectVersionPhotos(projectId: string, projectVersionId: number): Observable<PhotoUnion[] | undefined> {
        return this.graphDotnetService.query(graphql`
                query getProjectVersionPhotos($projectId: UUID!, $projectVersionId: Int!) {
                    project(id: $projectId) {
                        version(id: $projectVersionId) {
                            id
                            photos {
                                __typename
                                ... on MissingPhoto {
                                    urn
                                }
                                ... on Photo {
                                    urn
                                    dateUploadedUtc
                                    dateTakenUtc
                                    width
                                    height
                                    thumbnail
                                    print
                                }
                            }
                        }
                    }
                }
            `,
            { projectId, projectVersionId }
        ).pipe(map((result) => result.project?.version?.photos));
    }

    deleteProject(input: DeleteProjectInput): Observable<void> {
        return this.graphDotnetService.mutate<MediaclipMutationDeleteProjectArgs>(graphql`
                mutation deleteProject($input: DeleteProjectInput!) {
                    deleteProject(input: $input)
                }
            `, { input }
        ).pipe(map(x => x.deleteProject));
    }

    cancelProjectDeletion(input: CancelProjectDeletionInput): Observable<void> {
        return this.graphDotnetService.mutate<MediaclipMutationDeleteProjectArgs>(graphql`
                mutation cancelProjectDeletion($input: CancelProjectDeletionInput!) {
                    cancelProjectDeletion(input: $input)
                }
            `, { input }
        ).pipe(map(x => x.deleteProject));
    }

    updateProjectContent(input: UpdateProjectVersionContentInput): Observable<any> {
        return this.graphDotnetService.mutate<MediaclipMutationUpdateProjectVersionContentArgs>(graphql`
                mutation updateProjectVersionContent($input: UpdateProjectVersionContentInput!) {
                    updateProjectVersionContent(input: $input)
                }
            `, { input }
        ).pipe(map(x => x.deleteProject));
    }

    downloadProjectVersion(input: DownloadProjectVersionInput): Observable<DownloadProjectVersion> {
        return this.graphDotnetService.mutate<MediaclipMutationDownloadProjectVersionArgs>(graphql`
                mutation downloadProjectVersion($input: DownloadProjectVersionInput!) {
                    downloadProjectVersion(input: $input) {
                        downloadUrl
                    }
                }
            `, { input }
        ).pipe(map(x => x.downloadProjectVersion));
    }

    simulateProjectExpiration(input: SimulateProjectExpirationInput): Observable<SimulateProjectExpiration> {
        return this.graphDotnetService.mutate<MediaclipMutationSimulateProjectExpirationArgs>(graphql`
                mutation simulateProjectExpiration($input: SimulateProjectExpirationInput!) {
                    simulateProjectExpiration(input: $input) {
                        expiringProjectsApiUrl
                    }
                }
            `, { input }
        ).pipe(map(x => x.simulateProjectExpiration));
    }

    invalidateProjectStorageExternalPhotosCache(projectVersionId: number) {
        return this.graphDotnetService.mutate<MediaclipMutationInvalidateProjectStorageExternalPhotosCacheArgs>(graphql`
                mutation invalidateProjectStorageExternalPhotosCache($projectVersionId: Int!) {
                    invalidateProjectStorageExternalPhotosCache(projectVersionId: $projectVersionId) {
                        count
                    }
                }
            `, { projectVersionId }
        ).pipe(map(x => x.invalidateProjectStorageExternalPhotosCache));
    }
}
